import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBoundStore } from "store";
import { userService } from "users/services";
import { IUserRoleSelectOption } from "users/models";
import * as lookupService from "admin/services/LookupService";
import { ILookupValue } from "admin/services/LookupService";
import * as invitationService from "admin/services/InvitationService";
import * as salesConfigurationService from "sales/services/SalesConfigurationService";
import { ISalesConfiguration } from "sales/models";
import { salesKeys } from "sales/queries";

// Query keys factory
export const adminKeys = {
  all: ["admin"] as const,
  userSelectOptions: (tenantId?: string, organizationId?: string) =>
    ["admin", "users", "selectOptions", tenantId, organizationId] as const,
  tenantUserSelectOptions: (tenantId?: string) =>
    ["admin", "tenantUsers", "selectOptions", tenantId] as const,
  auditorSelectOptions: (tenantId?: string, organizationId?: string) =>
    ["admin", "users", "auditorSelectOptions", tenantId, organizationId] as const,
  accountManagerSelectOptions: (tenantId?: string, organizationId?: string) =>
    ["admin", "users", "accountManagerSelectOptions", tenantId, organizationId] as const,
  userDetails: (tenantId?: string, organizationId?: string, userId?: string) =>
    ["admin", "users", "details", tenantId, organizationId, userId] as const,
  invitations: (tenantId?: string) =>
    ["admin", "invitations", tenantId] as const,
};

/**
 * Fetches users for dropdown selection in admin.
 */
export function useUserSelectOptions() {
  const { tenant, currentOrganization } = useBoundStore();

  return useQuery({
    queryKey: adminKeys.userSelectOptions(tenant?.id, currentOrganization?.id),
    queryFn: () => userService.getUserSelectOptions(tenant!.id, currentOrganization!.id),
    enabled: !!tenant?.id && !!currentOrganization?.id,
    staleTime: 5 * 60_000,
    select: (data): IUserRoleSelectOption[] => data ?? [],
  });
}

/**
 * Fetches all tenant users with their tenant roles for admin management.
 */
export function useTenantUserSelectOptions() {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: adminKeys.tenantUserSelectOptions(tenant?.id),
    queryFn: () => userService.getTenantUserSelectOptions(tenant!.id),
    enabled: !!tenant?.id,
    staleTime: 5 * 60_000,
    select: (data): IUserRoleSelectOption[] => data ?? [],
  });
}

/**
 * Updates a user's tenant role.
 */
export function useUpdateTenantUserRole() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      userService.updateTenantUserRole(tenant!.id, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.tenantUserSelectOptions(tenant?.id),
      });
    },
  });
}

/**
 * Fetches auditors (users with field_auditor role) for dropdown selection.
 */
export function useAuditorSelectOptions() {
  const { tenant, currentOrganization } = useBoundStore();

  return useQuery({
    queryKey: adminKeys.auditorSelectOptions(tenant?.id, currentOrganization?.id),
    queryFn: () => userService.getAuditorSelectOptions(tenant!.id, currentOrganization!.id),
    enabled: !!tenant?.id && !!currentOrganization?.id,
    staleTime: 5 * 60_000,
    select: (data): IUserRoleSelectOption[] => data ?? [],
  });
}

/**
 * Fetches users assigned as account managers in sales configurations.
 */
export function useAccountManagerSelectOptions() {
  const { tenant, currentOrganization } = useBoundStore();

  return useQuery({
    queryKey: adminKeys.accountManagerSelectOptions(tenant?.id, currentOrganization?.id),
    queryFn: () => userService.getAccountManagerSelectOptions(tenant!.id, currentOrganization!.id),
    enabled: !!tenant?.id && !!currentOrganization?.id,
    staleTime: 5 * 60_000,
    select: (data): IUserRoleSelectOption[] => data ?? [],
  });
}

/**
 * Assigns a tenant user to an organization with a given role.
 */
export function useAssignUserToOrganization() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      userService.assignUserToOrganization(
        tenant!.id,
        currentOrganization!.id,
        userId,
        role
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userSelectOptions(tenant?.id, currentOrganization?.id),
      });
    },
  });
}

/**
 * Updates a user's organization role.
 */
export function useUpdateOrganizationRole() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      userService.updateOrganizationUserRole(
        tenant!.id,
        currentOrganization!.id,
        userId,
        role
      ),
    onSuccess: () => {
      // Invalidate user select options to refresh list
      queryClient.invalidateQueries({
        queryKey: adminKeys.userSelectOptions(tenant?.id, currentOrganization?.id),
      });
    },
  });
}

/**
 * Removes a user from the current organization (revokes access, keeps history).
 */
export function useRemoveOrganizationUserMutation() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: (userId: string) =>
      userService.removeOrganizationUser(
        tenant!.id,
        currentOrganization!.id,
        userId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userSelectOptions(tenant?.id, currentOrganization?.id),
      });
    },
  });
}

/**
 * Removes a user from the tenant (revokes tenant and organization access, keeps history).
 */
export function useRemoveTenantUserMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: (userId: string) => userService.removeTenantUser(tenant!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/**
 * Deletes a user.
 */
export function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userSelectOptions(tenant?.id, currentOrganization?.id),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Lookup queries
// ---------------------------------------------------------------------------

export const lookupKeys = {
  all: ["lookups"] as const,
  list: () => [...lookupKeys.all, "list"] as const,
  byCategory: (category?: string, includeInactive = false) =>
    [...lookupKeys.all, "category", category ?? "all", includeInactive ? "all-states" : "active"] as const,
};

/**
 * Fetches lookups, optionally filtered by category. Pass includeInactive to
 * surface deactivated values for admin management (consumers omit it so they
 * only see active options).
 */
export function useLookups(
  category?: string,
  options?: { enabled?: boolean; includeInactive?: boolean }
) {
  const { includeInactive = false, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey: category ? lookupKeys.byCategory(category, includeInactive) : lookupKeys.list(),
    queryFn: () =>
      category
        ? lookupService.getLookupsByCategory(category, includeInactive)
        : lookupService.getLookups(),
    staleTime: 5 * 60_000,
    ...queryOptions,
  });
}

/**
 * Fetches lookups for a specific category and transforms them into select options,
 * filtering to only active values.
 */
export function useLookupOptions(category: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: lookupKeys.byCategory(category),
    queryFn: () => lookupService.getLookupsByCategory(category),
    staleTime: 5 * 60_000,
    select: (data: ILookupValue[]) =>
      (data ?? [])
        .filter((item) => item.isActive)
        .map((item) => ({ value: item.value, label: item.label })),
    ...options,
  });
}

/**
 * Mutation to create a new lookup value.
 * Invalidates lookup queries on success.
 */
export function useCreateLookupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { category: string; value: string; label: string; sortOrder?: number }) =>
      lookupService.createLookup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupKeys.all });
    },
  });
}

/**
 * Mutation to update a lookup value (rename, activate/deactivate, reorder).
 * Invalidates lookup queries on success.
 */
export function useUpdateLookupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { label?: string; isActive?: boolean; sortOrder?: number };
    }) => lookupService.updateLookup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupKeys.all });
    },
  });
}

/**
 * Mutation to delete a lookup value.
 * Invalidates lookup queries on success.
 */
export function useDeleteLookupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => lookupService.deleteLookup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Sales configuration queries
// ---------------------------------------------------------------------------

/**
 * Mutation to delete a sales configuration.
 * Invalidates sales configuration queries on success.
 */
export function useDeleteSalesConfigurationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => salesConfigurationService.deleteSalesConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesKeys.configurations() });
    },
  });
}

/**
 * Mutation to create or update a sales configuration.
 * Invalidates sales configuration queries on success.
 */
export function useUpsertSalesConfigurationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ISalesConfiguration) =>
      salesConfigurationService.upsertSalesConfiguration(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesKeys.configurations() });
    },
  });
}

// ---------------------------------------------------------------------------
// Invitation queries
// ---------------------------------------------------------------------------

export function useInvitations(status?: string) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: adminKeys.invitations(tenant?.id),
    queryFn: () => invitationService.getInvitations(tenant!.id, status),
    enabled: !!tenant?.id,
    staleTime: 30_000,
  });
}

export function useInviteUserMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: ({ email, tenantRole }: { email: string; tenantRole: string }) =>
      invitationService.inviteUser(tenant!.id, email, tenantRole),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.invitations(tenant?.id),
      });
    },
  });
}

export function useRevokeInvitationMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: (invitationId: string) =>
      invitationService.revokeInvitation(tenant!.id, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.invitations(tenant?.id),
      });
    },
  });
}

export function useResendInvitationMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: (invitationId: string) =>
      invitationService.resendInvitation(tenant!.id, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.invitations(tenant?.id),
      });
    },
  });
}
